import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
	fetchGA4Aggregate,
	fetchGA4PageMetrics,
	fetchGA4TrafficSources,
	type Period,
} from "@/services/ga4Service";
import { useTenantStore } from "@/stores/useTenantStore";
import { Button } from "@/components/ui/button";
import { SectionPanel } from "@/components/dashboard/SectionPanel";
import { PeriodSelector } from "@/components/dashboard/PeriodSelector";
import { IntegrationStatePanel } from "@/components/dashboard/IntegrationStatePanel";
import { EngagementTable } from "@/components/dashboard/tables/EngagementTable";
import { TrafficSourcesList } from "@/components/dashboard/tables/TrafficSourcesList";
import { AudienceSplitBar } from "@/components/dashboard/tables/AudienceSplitBar";

const PERIOD_DAYS: Record<Period, number> = { "7d": 7, "28d": 28, "90d": 90 };

function parsePeriod(raw: string | null): Period {
	return raw === "7d" || raw === "90d" ? raw : "28d";
}

function getApiErrorMessage(error: unknown, fallback: string) {
	return (
		(error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
		(error as Error)?.message ||
		fallback
	);
}

/** GA4 signals "not configured" via an error body — that is an activation state, not a failure. */
function isNotConfiguredError(error: unknown): boolean {
	return /not configured/i.test(getApiErrorMessage(error, ""));
}

export default function TrafficPage() {
	const { currentTenant } = useTenantStore();
	const tenantScope = currentTenant ?? "no-tenant";
	const [searchParams, setSearchParams] = useSearchParams();
	const period = parsePeriod(searchParams.get("period"));

	const setPeriod = (p: Period) => {
		const next = new URLSearchParams(searchParams);
		next.set("period", p);
		setSearchParams(next, { replace: true });
	};

	// Single connection probe: all panels derive not_connected from this one
	// query so they can never disagree on the GA4 connection state. Since D3 it
	// is ALSO the audience data source (new/returning users): a non-config agg
	// failure still never gates the other panels — each renders its own query's
	// state — but the Audience panel surfaces it as its own named error.
	const { data: ga4Agg, isLoading: aggLoading, isError: aggError, error: aggErrorObj, refetch: aggRefetch } = useQuery({
		queryKey: ["ga4", tenantScope, "aggregate", period],
		queryFn: () => fetchGA4Aggregate(currentTenant, period),
		staleTime: 5 * 60 * 1000,
		enabled: !!currentTenant,
		retry: false,
	});
	const { data: ga4Pages, isLoading: pagesLoading, isError: pagesError, error: pagesErrorObj, refetch: pagesRefetch } = useQuery({
		queryKey: ["ga4", tenantScope, "page-metrics", period],
		queryFn: () => fetchGA4PageMetrics(currentTenant, period),
		staleTime: 5 * 60 * 1000,
		enabled: !!currentTenant,
		retry: false,
	});
	const { data: ga4Sources, isLoading: sourcesLoading, isError: sourcesError, error: sourcesErrorObj, refetch: sourcesRefetch } = useQuery({
		queryKey: ["ga4", tenantScope, "traffic-sources", period],
		queryFn: () => fetchGA4TrafficSources(currentTenant, period),
		staleTime: 5 * 60 * 1000,
		enabled: !!currentTenant,
		retry: false,
	});

	const ga4NotConfigured = aggError && isNotConfiguredError(aggErrorObj);

	// Audience split (doc 15 §8): null fields are treated as no data, never as
	// 0 — a 0%/0% ghost pair is a doc 50 PITFALL-8 violation.
	const newUsers = ga4Agg?.new_users ?? 0;
	const returningUsers = ga4Agg?.returning_users ?? 0;
	const hasAudience = newUsers + returningUsers > 0;

	const notConnectedBody = (
		<div className="space-y-3 p-6">
			<p className="text-sm text-foreground">
				Traffic detail comes from GA4 — connect to see page engagement and where visits come from.
			</p>
			<div>
				<Button asChild className="bg-coral text-primary-foreground hover:bg-coral-hover">
					<Link to="/connect/ga4">Connect GA4</Link>
				</Button>
			</div>
		</div>
	);

	const caption = (ga4Agg || ga4Pages || ga4Sources) ? `Last ${PERIOD_DAYS[period]} days · through today · GA4` : undefined;

	return (
		<div className="w-full space-y-6">
			<div className="flex flex-wrap items-center justify-between gap-4">
				<h1 className="text-dash-page font-semibold text-foreground">Traffic</h1>
				<PeriodSelector value={period} onChange={setPeriod} />
			</div>
			<SectionPanel
				title={`Engagement by Page (${ga4Pages?.total ?? 0})`}
				caption={caption}
				surfaceEmphasis="primary"
			>
				{aggLoading || pagesLoading ? (
					<IntegrationStatePanel state="loading" skeleton="rows" />
				) : ga4NotConfigured ? (
					notConnectedBody
				) : pagesError ? (
					<IntegrationStatePanel state="error" errorReason={getApiErrorMessage(pagesErrorObj, "Couldn't load page engagement data")} onRetry={() => pagesRefetch()} />
				) : !ga4Pages || ga4Pages.pages.length === 0 ? (
					<IntegrationStatePanel state="empty" message="No engagement data found for this period" />
				) : (
					<EngagementTable pages={ga4Pages.pages} />
				)}
			</SectionPanel>
			{/* Not-configured: the Audience panel folds OUT entirely — the two D1
			    CTA bodies already cover the page; a third copy would be noise.
			    Upstream (non-config) agg failure stays IN as the panel's own
			    named error + Retry, exactly like its siblings. */}
			{!ga4NotConfigured && (
				<SectionPanel title="Audience" caption={caption}>
					{aggLoading ? (
						<IntegrationStatePanel state="loading" skeleton="rows" />
					) : aggError ? (
						<IntegrationStatePanel state="error" errorReason={getApiErrorMessage(aggErrorObj, "Couldn't load audience data")} onRetry={() => aggRefetch()} />
					) : !hasAudience ? (
						<IntegrationStatePanel state="empty" message={`No audience data in the last ${PERIOD_DAYS[period]} days`} />
					) : (
						<AudienceSplitBar newUsers={newUsers} returningUsers={returningUsers} />
					)}
				</SectionPanel>
			)}
			<SectionPanel title="Traffic Sources" caption={caption}>
				{aggLoading || sourcesLoading ? (
					<IntegrationStatePanel state="loading" skeleton="rows" />
				) : ga4NotConfigured ? (
					notConnectedBody
				) : sourcesError ? (
					<IntegrationStatePanel state="error" errorReason={getApiErrorMessage(sourcesErrorObj, "Couldn't load traffic source data")} onRetry={() => sourcesRefetch()} />
				) : !ga4Sources || ga4Sources.length === 0 ? (
					<IntegrationStatePanel state="empty" message="No traffic source data found for this period" />
				) : (
					<TrafficSourcesList sources={ga4Sources} />
				)}
			</SectionPanel>
		</div>
	);
}
